import { PRIMARY_FONT, SECONDARY_FONT, FontWeights } from '@pff-consumer/core-ui'
import type { ColorValue, TextStyle } from 'react-native'
import { NativeUiTheme } from '../../styles/theme'
import { getFontStyles } from '../../styles/create-use-fonts'

/**
 * 🚨🚨🚨 DO NOT ADD ANY NEW TOKENS WITHOUT CONSULTING THE DESIGN TEAM    🚨🚨🚨
 * 🚨🚨🚨 NEVER REFERENCE THESE TOKENS OUTSIDE THE SIBLING TEXT COMPONENT 🚨🚨🚨
 * Font weight naming
 * 400 - Regular
 * 600 - SemiBold
 * 700 - Bold
 * 800 - XBold
 * 900 - Black
 */

export type TextTokens = {
  [key: string]: TextStyle
}

export const useGenerateTokens = (theme: NativeUiTheme, textColor: ColorValue): TextTokens => {
  const TextColor = {
    color: textColor,
  }

  // Primary Fonts (Archivo)
  const Text2XSRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(10),
    lineHeight: theme.calcUnit(14),
  }

  const Text2XSSemiBold: TextStyle = {
    ...Text2XSRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.SEMIBOLD),
  }

  const Text2XSSemiBoldItalic: TextStyle = {
    ...Text2XSSemiBold,
    fontStyle: 'italic',
  }

  const Text2XSXBold: TextStyle = {
    ...Text2XSRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.XBOLD),
  }

  const Text2XSXBoldItalic: TextStyle = {
    ...Text2XSXBold,
    fontStyle: 'italic',
  }

  const TextXSRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(12),
    lineHeight: theme.calcUnit(16),
  }

  const TextXSRegularItalic: TextStyle = {
    ...TextXSRegular,
    fontStyle: 'italic',
  }

  const TextXSSemiBold: TextStyle = {
    ...TextXSRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.SEMIBOLD),
  }

  const TextXSSemiBoldItalic: TextStyle = {
    ...TextXSSemiBold,
    fontStyle: 'italic',
  }

  const TextXSBold: TextStyle = {
    ...TextXSRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BOLD),
  }

  const TextXSBoldItalic: TextStyle = {
    ...TextXSBold,
    fontStyle: 'italic',
  }

  const TextSmallRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(14),
    lineHeight: theme.calcUnit(20),
  }

  const TextSmallSemiBold: TextStyle = {
    ...TextSmallRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.SEMIBOLD),
  }

  const TextSmallBold: TextStyle = {
    ...TextSmallRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BOLD),
  }

  const TextSmallXBold: TextStyle = {
    ...TextSmallRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.XBOLD),
  }

  const TextBaseRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(16),
    lineHeight: theme.calcUnit(24),
  }

  const TextBaseSemiBold: TextStyle = {
    ...TextBaseRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.SEMIBOLD),
  }

  const TextBaseBold: TextStyle = {
    ...TextBaseRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BOLD),
  }

  const TextBaseBoldItalic: TextStyle = {
    ...TextBaseBold,
    fontStyle: 'italic',
  }

  const TextBaseXBold: TextStyle = {
    ...TextBaseRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.XBOLD),
  }

  const TextBaseBlack: TextStyle = {
    ...TextBaseRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BLACK),
  }

  const TextBodyRegular: TextStyle = {
    ...TextColor,
    color: theme.colors.contentColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(18),
    lineHeight: theme.calcUnit(26),
  }

  const TextBodyRegularItalic: TextStyle = {
    ...TextBodyRegular,
    fontStyle: 'italic',
  }

  const TextBodyBold: TextStyle = {
    ...TextBodyRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BOLD),
  }

  const TextBodyBoldItalic: TextStyle = {
    ...TextBodyBold,
    fontStyle: 'italic',
  }

  const TextMdRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(20),
    lineHeight: theme.calcUnit(28),
  }

  const TextMdSemiBold: TextStyle = {
    ...TextMdRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.SEMIBOLD),
  }

  const TextMdXBold: TextStyle = {
    ...TextMdRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.XBOLD),
  }

  const TextMdBlack: TextStyle = {
    ...TextMdRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BLACK),
  }

  const TextLgRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(24),
    lineHeight: theme.calcUnit(32),
  }

  const TextLgBold: TextStyle = {
    ...TextLgRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BOLD),
  }

  const TextLgBoldItalic: TextStyle = {
    ...TextLgBold,
    fontStyle: 'italic',
  }

  const TextLgBlack: TextStyle = {
    ...TextLgRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BLACK),
  }

  const TextXLXBold: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.XBOLD),
    fontSize: theme.calcUnit(28),
    lineHeight: theme.calcUnit(36),
  }

  const TextXLBlack: TextStyle = {
    ...TextXLXBold,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BLACK),
  }

  const Text2XLRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(32),
    lineHeight: theme.calcUnit(38),
  }

  const Text2XLBold: TextStyle = {
    ...Text2XLRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BOLD),
  }

  const Text2XLBlack: TextStyle = {
    ...Text2XLRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BLACK),
  }

  const Text3XLXBold: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.XBOLD),
    fontSize: theme.calcUnit(36),
    lineHeight: theme.calcUnit(44),
    letterSpacing: theme.calcUnit(-2),
  }

  const Text3XLBlack: TextStyle = {
    ...Text3XLXBold,
    fontWeight: FontWeights.BLACK,
  }

  const Text4XLRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(40),
    lineHeight: theme.calcUnit(46),
  }

  const Text4XLBlack: TextStyle = {
    ...Text4XLRegular,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BLACK),
  }

  const Text5XLXBold: TextStyle = {
    ...TextColor,
    ...getFontStyles(PRIMARY_FONT, FontWeights.XBOLD),
    fontSize: theme.calcUnit(46),
    lineHeight: theme.calcUnit(52),
    letterSpacing: theme.calcUnit(-2),
  }

  const Text5XLBlack: TextStyle = {
    ...Text5XLXBold,
    ...getFontStyles(PRIMARY_FONT, FontWeights.BLACK),
  }

  // Primary Fonts (Archivo)
  const Text2XSNarrowRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(SECONDARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(10),
    lineHeight: theme.calcUnit(14),
    letterSpacing: theme.calcUnit(0.2),
  }

  const Text2XSNarrowSemiBold: TextStyle = {
    ...Text2XSNarrowRegular,
    ...getFontStyles(SECONDARY_FONT, FontWeights.SEMIBOLD),
  }

  const Text2XSNarrowBold: TextStyle = {
    ...Text2XSNarrowRegular,
    ...getFontStyles(SECONDARY_FONT, FontWeights.BOLD),
  }

  const TextXSNarrowRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(SECONDARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(12),
    lineHeight: theme.calcUnit(16),
    letterSpacing: theme.calcUnit(0.2),
  }

  const TextXSNarrowSemiBold: TextStyle = {
    ...TextXSNarrowRegular,
    ...getFontStyles(SECONDARY_FONT, FontWeights.SEMIBOLD),
  }

  const TextXSNarrowBold: TextStyle = {
    ...TextXSNarrowRegular,
    ...getFontStyles(SECONDARY_FONT, FontWeights.BOLD),
  }

  const TextSmallNarrowRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(SECONDARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(14),
    lineHeight: theme.calcUnit(20),
  }

  const TextSmallNarrowBold: TextStyle = {
    ...TextSmallNarrowRegular,
    ...getFontStyles(SECONDARY_FONT, FontWeights.BOLD),
  }

  const TextBaseNarrowRegular: TextStyle = {
    ...TextColor,
    ...getFontStyles(SECONDARY_FONT, FontWeights.REGULAR),
    fontSize: theme.calcUnit(16),
    lineHeight: theme.calcUnit(24),
  }

  const TextBaseNarrowBold: TextStyle = {
    ...TextBaseNarrowRegular,
    ...getFontStyles(SECONDARY_FONT, FontWeights.BOLD),
  }

  return {
    // Primary Fonts (ARCHIVO)
    Text2XSRegular,
    Text2XSSemiBold,
    Text2XSSemiBoldItalic,
    Text2XSXBold,
    Text2XSXBoldItalic,
    TextXSRegular,
    TextXSRegularItalic,
    TextXSSemiBold,
    TextXSSemiBoldItalic,
    TextXSBold,
    TextXSBoldItalic,
    TextSmallRegular,
    TextSmallSemiBold,
    TextSmallBold,
    TextSmallXBold,
    TextBaseRegular,
    TextBaseSemiBold,
    TextBaseBold,
    TextBaseBoldItalic,
    TextBaseXBold,
    TextBaseBlack,
    TextBodyRegular,
    TextBodyRegularItalic,
    TextBodyBold,
    TextBodyBoldItalic,
    TextMdRegular,
    TextMdSemiBold,
    TextMdXBold,
    TextMdBlack,
    TextLgRegular,
    TextLgBold,
    TextLgBoldItalic,
    TextLgBlack,
    TextXLXBold,
    TextXLBlack,
    Text2XLRegular,
    Text2XLBold,
    Text2XLBlack,
    Text3XLXBold,
    Text3XLBlack,
    Text4XLRegular,
    Text4XLBlack,
    Text5XLXBold,
    Text5XLBlack,
    // Primary Fonts (ARCHIVO NARROW)
    Text2XSNarrowRegular,
    Text2XSNarrowSemiBold,
    Text2XSNarrowBold,
    TextXSNarrowRegular,
    TextXSNarrowSemiBold,
    TextXSNarrowBold,
    TextSmallNarrowRegular,
    TextSmallNarrowBold,
    TextBaseNarrowRegular,
    TextBaseNarrowBold,
  }
}
